import { useEffect, useState, type FormEvent } from 'react';
import { Clock } from 'lucide-react';
import {
  ApiError,
  members as membersApi,
  type MemberRole,
  type MembersResponse,
} from '@/lib/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { SettingsSubNav } from '@/components/SettingsSubNav';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Badge, Button, Surface } from '@/components/ui';

export default function Members() {
  const [data, setData] = useState<MembersResponse | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  // Invite form state
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRole>('member');
  const [inviting, setInviting] = useState(false);
  const [inviteErr, setInviteErr] = useState<string | null>(null);
  const [inviteSent, setInviteSent] = useState<string | null>(null); // last invited email

  // Remove confirmation
  const [pendingRemove, setPendingRemove] = useState<{ id: string; label: string; isInvite: boolean } | null>(null);

  function reload() {
    membersApi
      .list()
      .then(setData)
      .catch((e) => setLoadErr((e as Error).message || 'Failed to load members.'));
  }

  useEffect(() => {
    reload();
  }, []);

  async function onInvite(e: FormEvent) {
    e.preventDefault();
    setInviting(true);
    setInviteErr(null);
    setInviteSent(null);
    try {
      await membersApi.invite(email.trim().toLowerCase(), role);
      setInviteSent(email);
      setEmail('');
      reload();
    } catch (err) {
      setInviteErr(
        err instanceof ApiError ? inviteErrorFor(err.code) : (err as Error).message,
      );
    } finally {
      setInviting(false);
    }
  }

  async function changeRole(id: string, newRole: MemberRole) {
    try {
      await membersApi.setRole(id, newRole);
      reload();
    } catch (e) {
      alert((e as Error).message);
    }
  }

  async function doRemove() {
    if (!pendingRemove) return;
    await membersApi.remove(pendingRemove.id);
    reload();
  }

  if (loadErr) {
    return (
      <DashboardLayout title="Team">
        <p className="mt-6 text-sm text-[var(--color-destructive)]">{loadErr}</p>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout title="Team">
        <div className="mt-7 space-y-3">
          <div className="h-32 animate-pulse rounded-2xl bg-[var(--surface-glass)]" />
          <div className="h-32 animate-pulse rounded-2xl bg-[var(--surface-glass)]" />
        </div>
      </DashboardLayout>
    );
  }

  const usedSeats = data.members.length + data.invitations.length;
  const canInvite =
    (data.role === 'owner' || data.role === 'admin') && usedSeats < data.memberLimit;
  const isFree = data.plan === 'free';

  return (
    <DashboardLayout
      title="Team"
      titleAction={
        <span className="text-sm text-[var(--color-on-surface-variant)]">
          {usedSeats} / {data.memberLimit} seats · {data.plan} plan
        </span>
      }
    >
      <SettingsSubNav />

      {/* Invite section */}
      <Surface tone="raised" className="mt-7 p-6">
        <h2 className="font-display text-lg font-bold tracking-tight">Invite someone</h2>
        {isFree ? (
          <Surface tone="deep" className="mt-4 p-4 text-sm">
            <p className="font-medium">Free plan is solo only.</p>
            <p className="mt-1.5 text-[var(--color-on-surface-variant)]">
              Upgrade to Pro or Agency to invite up to 3 teammates. They&apos;ll
              be able to create links, manage domains, and view analytics in
              this workspace.
            </p>
          </Surface>
        ) : (
          <form onSubmit={onInvite} className="mt-4 flex flex-wrap items-end gap-3">
            <label className="flex min-w-[200px] flex-1 flex-col gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-[0.06em] text-[var(--color-on-surface-variant)]">
                Email
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="teammate@yourbrand.com"
                required
                disabled={!canInvite || inviting}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--surface-deep)] px-4 py-2.5 outline-none transition focus:border-[var(--color-ring)] disabled:opacity-50"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-[0.06em] text-[var(--color-on-surface-variant)]">
                Role
              </span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as MemberRole)}
                disabled={!canInvite || inviting}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--surface-deep)] px-4 py-2.5 outline-none transition focus:border-[var(--color-ring)] disabled:opacity-50"
              >
                <option value="admin">Admin — full access, can invite</option>
                <option value="member">Member — manage links + domains</option>
              </select>
            </label>
            <Button type="submit" disabled={!canInvite || inviting || !email}>
              {inviting ? 'Inviting…' : 'Send invite'}
            </Button>
          </form>
        )}
        {inviteErr && (
          <p className="mt-3 text-sm text-[var(--color-destructive)]">{inviteErr}</p>
        )}
        {inviteSent && (
          <p className="mt-3 text-sm text-[var(--color-secondary)]">
            ✓ Invitation email sent to{' '}
            <code className="font-mono">{inviteSent}</code>. They have 48 hours
            to accept.
          </p>
        )}
        {!isFree && !canInvite && usedSeats >= data.memberLimit && (
          <p className="mt-3 text-sm text-[var(--color-on-surface-variant)]">
            You&apos;ve filled all {data.memberLimit} seats. Remove someone or
            upgrade to add more.
          </p>
        )}
      </Surface>

      {/* Members list */}
      <h2 className="mt-10 font-display text-xl font-bold tracking-tight">Active members</h2>
      <Surface className="mt-3 divide-y divide-[var(--color-border)] overflow-hidden">
        {data.members.map((m) => (
          <div key={m.id} className="flex items-center gap-4 p-4">
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-display text-sm font-bold"
              style={{
                background: 'linear-gradient(135deg, #cdbdff 0%, #7c4dff 100%)',
                color: '#370096',
              }}
            >
              {m.image ? (
                <img src={m.image} alt="" className="h-10 w-10 rounded-full object-cover" />
              ) : (
                (m.name || m.email).slice(0, 1).toUpperCase()
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 truncate font-medium">
                {m.name || m.email.split('@')[0]}
                {m.isYou && <Badge tone="primary">You</Badge>}
              </div>
              <div className="truncate text-xs text-[var(--color-on-surface-variant)]">
                {m.email}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {m.role === 'owner' || m.isYou || (data.role !== 'owner' && data.role !== 'admin') ? (
                <Badge tone={m.role === 'owner' ? 'primary' : 'neutral'}>{m.role}</Badge>
              ) : (
                <select
                  value={m.role}
                  onChange={(e) => changeRole(m.id, e.target.value as MemberRole)}
                  className="rounded-full border border-[var(--color-border)] bg-[var(--surface-deep)] px-3 py-1.5 text-xs"
                >
                  <option value="admin">Admin</option>
                  <option value="member">Member</option>
                </select>
              )}
              {m.role !== 'owner' && !m.isYou && (data.role === 'owner' || data.role === 'admin') && (
                <button
                  onClick={() =>
                    setPendingRemove({
                      id: m.id,
                      label: m.name || m.email,
                      isInvite: false,
                    })
                  }
                  className="text-xs text-[var(--color-destructive)] hover:underline"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
      </Surface>

      {/* Pending invites */}
      {data.invitations.length > 0 && (
        <>
          <h2 className="mt-10 font-display text-xl font-bold tracking-tight">
            Pending invitations
          </h2>
          <Surface className="mt-3 divide-y divide-[var(--color-border)] overflow-hidden">
            {data.invitations.map((inv) => (
              <div key={inv.id} className="flex items-center gap-4 p-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-dashed border-[var(--color-border)] text-[var(--color-on-surface-variant)]">
                  <Clock size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{inv.email}</div>
                  <div className="text-xs text-[var(--color-on-surface-variant)]">
                    Invited as {inv.role} · expires{' '}
                    {new Date(inv.expiresAt).toLocaleDateString()}
                  </div>
                </div>
                <button
                  onClick={() =>
                    setPendingRemove({ id: inv.id, label: inv.email, isInvite: true })
                  }
                  className="text-xs text-[var(--color-destructive)] hover:underline"
                >
                  Cancel
                </button>
              </div>
            ))}
          </Surface>
        </>
      )}

      <ConfirmDialog
        open={pendingRemove !== null}
        onClose={() => setPendingRemove(null)}
        onConfirm={doRemove}
        title={
          pendingRemove?.isInvite
            ? `Cancel invite to ${pendingRemove.label}?`
            : `Remove ${pendingRemove?.label}?`
        }
        description={
          pendingRemove?.isInvite
            ? "They'll lose access to the invitation link. You can re-invite anytime."
            : "They'll be signed out of this workspace and lose access to all links, domains, and analytics. They can be re-invited later."
        }
        confirmLabel={pendingRemove?.isInvite ? 'Cancel invite' : 'Remove member'}
        destructive
      />
    </DashboardLayout>
  );
}

function inviteErrorFor(code: string): string {
  switch (code) {
    case 'not_authorized':
      return 'Only owners and admins can invite teammates.';
    case 'email_invalid':
      return "That doesn't look like a valid email address.";
    case 'role_invalid':
      return 'Pick a valid role.';
    case 'plan_limit_reached':
      return "You've reached your plan's seat limit. Upgrade or remove someone first.";
    case 'user_already_member':
      return 'That email is already a member of this workspace.';
    case 'invite_already_pending':
      return 'An invitation is already pending for that email.';
    default:
      return code;
  }
}
